import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { landingPageResolver } from 'app/modules/landing/public-page.resolvers';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to landing page
    { path: '', pathMatch: 'full', redirectTo: 'landing' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'example' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') }
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {
                path: 'landing', children: [], // Empty route - content rendered in layout
                resolve: {
                    pageData: landingPageResolver,
                },
                data: {
                    title: 'Superformance - Iconic High-Performance Replica Vehicles',
                    description: 'Superformance builds world-class continuation and replica vehicles including the Shelby Cobra, GT40, Corvette Grand Sport, and more. Handcrafted performance and heritage.',
                    keywords: 'Superformance, replica cars, continuation vehicles, Shelby Cobra, GT40, Corvette Grand Sport, high-performance vehicles, classic cars',
                    ogTitle: 'Superformance - Iconic High-Performance Replica Vehicles',
                    ogDescription: 'Superformance builds world-class continuation and replica vehicles including the Shelby Cobra, GT40, Corvette Grand Sport, and more.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance - Iconic High-Performance Replica Vehicles',
                    twDescription: 'Superformance builds world-class continuation and replica vehicles including the Shelby Cobra, GT40, Corvette Grand Sport, and more.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com'
                }
            },
            { path: 'home', redirectTo: 'landing', pathMatch: 'full' },
            {
                path: 'vehicles', loadChildren: () => import('app/modules/landing/vehicles/vehicles.routes'),
                data: {
                    title: 'Superformance Vehicles - Browse Our Iconic Vehicle Lineup',
                    description: 'Explore the full range of Superformance vehicles including the MKIII Cobra, GT40, Corvette Grand Sport, and Shelby Daytona Coupe. Built with precision and passion.',
                    keywords: 'Superformance vehicles, MKIII Cobra, GT40, Corvette Grand Sport, Shelby Daytona Coupe, replica vehicles, continuation cars',
                    ogTitle: 'Superformance Vehicles - Browse Our Iconic Vehicle Lineup',
                    ogDescription: 'Explore the full range of Superformance vehicles including the MKIII Cobra, GT40, Corvette Grand Sport, and Shelby Daytona Coupe.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/vehicles',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Vehicles - Browse Our Iconic Vehicle Lineup',
                    twDescription: 'Explore the full range of Superformance vehicles including the MKIII Cobra, GT40, Corvette Grand Sport, and Shelby Daytona Coupe.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/vehicles'
                }
            },
            {
                path: 'dealers', loadChildren: () => import('app/modules/landing/dealers/dealers.routes'),
                data: {
                    title: 'Superformance Dealers - Find an Authorized Dealer Near You',
                    description: 'Locate an authorized Superformance dealer worldwide. Our dealer network provides sales, support, and expert guidance for your dream vehicle.',
                    keywords: 'Superformance dealers, authorized dealers, dealer locator, buy Superformance, vehicle dealers',
                    ogTitle: 'Superformance Dealers - Find an Authorized Dealer Near You',
                    ogDescription: 'Locate an authorized Superformance dealer worldwide. Our dealer network provides sales, support, and expert guidance for your dream vehicle.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/dealers',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Dealers - Find an Authorized Dealer Near You',
                    twDescription: 'Locate an authorized Superformance dealer worldwide. Our dealer network provides sales, support, and expert guidance.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/dealers'
                }
            },
            {
                path: 'faq', loadChildren: () => import('app/modules/landing/faq/faq.routes'),
                data: {
                    title: 'Superformance FAQ - Frequently Asked Questions',
                    description: 'Find answers to common questions about Superformance vehicles, ordering, customization, dealers, and more.',
                    keywords: 'Superformance FAQ, frequently asked questions, vehicle ordering, customization, replica car questions',
                    ogTitle: 'Superformance FAQ - Frequently Asked Questions',
                    ogDescription: 'Find answers to common questions about Superformance vehicles, ordering, customization, dealers, and more.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/faq',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance FAQ - Frequently Asked Questions',
                    twDescription: 'Find answers to common questions about Superformance vehicles, ordering, customization, dealers, and more.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/faq'
                }
            },
            {
                path: 'about', loadChildren: () => import('app/modules/landing/about/about.routes'),
                data: {
                    title: 'About Superformance - Our Story, Heritage & Mission',
                    description: 'Learn about Superformance, a world leader in building high-quality continuation and replica vehicles. Discover our history, craftsmanship, and commitment to automotive excellence.',
                    keywords: 'about Superformance, company history, vehicle manufacturer, continuation vehicles, automotive heritage, craftsmanship',
                    ogTitle: 'About Superformance - Our Story, Heritage & Mission',
                    ogDescription: 'Learn about Superformance, a world leader in building high-quality continuation and replica vehicles.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/about',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'About Superformance - Our Story, Heritage & Mission',
                    twDescription: 'Learn about Superformance, a world leader in building high-quality continuation and replica vehicles.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/about'
                }
            },
            {
                path: 'license', loadChildren: () => import('app/modules/landing/license/license.routes'),
                data: {
                    title: 'Superformance Licensing - Official Licensing Information',
                    description: 'Superformance is an officially licensed manufacturer of Shelby and other iconic vehicles. Learn about our licensing agreements and authenticity.',
                    keywords: 'Superformance license, Shelby license, official manufacturer, licensed replica vehicles',
                    ogTitle: 'Superformance Licensing - Official Licensing Information',
                    ogDescription: 'Superformance is an officially licensed manufacturer of Shelby and other iconic vehicles.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/license',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Licensing - Official Licensing Information',
                    twDescription: 'Superformance is an officially licensed manufacturer of Shelby and other iconic vehicles.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/license'
                }
            },
            {
                path: 'factory', loadChildren: () => import('app/modules/landing/factory/factory.routes'),
                data: {
                    title: 'Superformance Factory - Where Legends Are Built',
                    description: 'Take a look inside the Superformance factory. See how our world-class vehicles are handcrafted with precision engineering and attention to detail.',
                    keywords: 'Superformance factory, vehicle manufacturing, handcrafted cars, production facility, vehicle assembly',
                    ogTitle: 'Superformance Factory - Where Legends Are Built',
                    ogDescription: 'Take a look inside the Superformance factory. See how our world-class vehicles are handcrafted with precision engineering.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/factory',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Factory - Where Legends Are Built',
                    twDescription: 'Take a look inside the Superformance factory. See how our world-class vehicles are handcrafted with precision engineering.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/factory'
                }
            },
            {
                path: 'gallery', loadChildren: () => import('app/modules/landing/gallery/gallery.routes'),
                data: {
                    title: 'Superformance Gallery - Vehicle Photos & Media',
                    description: 'Browse stunning photos of Superformance vehicles. View our gallery of Cobras, GT40s, Corvette Grand Sports, and more in action and on display.',
                    keywords: 'Superformance gallery, vehicle photos, car images, Cobra photos, GT40 images, classic car gallery',
                    ogTitle: 'Superformance Gallery - Vehicle Photos & Media',
                    ogDescription: 'Browse stunning photos of Superformance vehicles. View our gallery of Cobras, GT40s, Corvette Grand Sports, and more.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/gallery',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Gallery - Vehicle Photos & Media',
                    twDescription: 'Browse stunning photos of Superformance vehicles. View our gallery of Cobras, GT40s, Corvette Grand Sports, and more.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/gallery'
                }
            },
            {
                path: 'inventory', loadChildren: () => import('app/modules/landing/inventory/inventory.routes'),
                data: {
                    title: 'Superformance Inventory - Available Vehicles For Sale',
                    description: 'Browse available Superformance vehicles ready for purchase. Find your dream Cobra, GT40, or Grand Sport from our current inventory.',
                    keywords: 'Superformance inventory, vehicles for sale, buy Cobra, buy GT40, available cars, in-stock vehicles',
                    ogTitle: 'Superformance Inventory - Available Vehicles For Sale',
                    ogDescription: 'Browse available Superformance vehicles ready for purchase. Find your dream Cobra, GT40, or Grand Sport from our current inventory.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/inventory',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Inventory - Available Vehicles For Sale',
                    twDescription: 'Browse available Superformance vehicles ready for purchase. Find your dream Cobra, GT40, or Grand Sport.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/inventory'
                }
            },
            {
                path: 'testimonials', loadChildren: () => import('app/modules/landing/testimonials/testimonials.routes'),
                data: {
                    title: 'Superformance Testimonials - What Our Owners Say',
                    description: 'Read testimonials from Superformance vehicle owners. Discover why enthusiasts worldwide choose Superformance for their dream car.',
                    keywords: 'Superformance testimonials, owner reviews, customer stories, vehicle reviews, owner experiences',
                    ogTitle: 'Superformance Testimonials - What Our Owners Say',
                    ogDescription: 'Read testimonials from Superformance vehicle owners. Discover why enthusiasts worldwide choose Superformance.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/testimonials',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Testimonials - What Our Owners Say',
                    twDescription: 'Read testimonials from Superformance vehicle owners. Discover why enthusiasts worldwide choose Superformance.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/testimonials'
                }
            },
            {
                path: 'news', loadChildren: () => import('app/modules/landing/news/news.routes'),
                data: {
                    title: 'Superformance News - Latest Updates & Announcements',
                    description: 'Stay up to date with the latest Superformance news, product announcements, and company updates.',
                    keywords: 'Superformance news, announcements, vehicle updates, company news, automotive news',
                    ogTitle: 'Superformance News - Latest Updates & Announcements',
                    ogDescription: 'Stay up to date with the latest Superformance news, product announcements, and company updates.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/news',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance News - Latest Updates & Announcements',
                    twDescription: 'Stay up to date with the latest Superformance news, product announcements, and company updates.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/news'
                }
            },
            {
                path: 'events', loadChildren: () => import('app/modules/landing/events/events.routes'),
                data: {
                    title: 'Superformance Events - Shows, Meets & Gatherings',
                    description: 'Find upcoming Superformance events, car shows, meets, and owner gatherings. Join the Superformance community.',
                    keywords: 'Superformance events, car shows, vehicle meets, owner gatherings, automotive events',
                    ogTitle: 'Superformance Events - Shows, Meets & Gatherings',
                    ogDescription: 'Find upcoming Superformance events, car shows, meets, and owner gatherings. Join the Superformance community.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/events',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Events - Shows, Meets & Gatherings',
                    twDescription: 'Find upcoming Superformance events, car shows, meets, and owner gatherings.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/events'
                }
            },
            {
                path: 'news-events', loadChildren: () => import('app/modules/landing/news-events/news-events.routes'),
                data: {
                    title: 'Superformance News & Events',
                    description: 'Browse Superformance news and upcoming events. Stay connected with the latest from the Superformance community.',
                    keywords: 'Superformance news, events, car shows, announcements, community',
                    ogTitle: 'Superformance News & Events',
                    ogDescription: 'Browse Superformance news and upcoming events. Stay connected with the latest from the Superformance community.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/news-events',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance News & Events',
                    twDescription: 'Browse Superformance news and upcoming events. Stay connected with the Superformance community.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/news-events'
                }
            },
            {
                path: 'contact', loadChildren: () => import('app/modules/landing/contact-us/contact-us.routes'),
                data: {
                    title: 'Contact Superformance - Get in Touch',
                    description: 'Contact Superformance for inquiries about our vehicles, dealers, orders, or any other questions. Our team is ready to assist you.',
                    keywords: 'contact Superformance, get in touch, vehicle inquiry, customer support, Superformance contact',
                    ogTitle: 'Contact Superformance - Get in Touch',
                    ogDescription: 'Contact Superformance for inquiries about our vehicles, dealers, orders, or any other questions.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/contact',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Contact Superformance - Get in Touch',
                    twDescription: 'Contact Superformance for inquiries about our vehicles, dealers, orders, or any other questions.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/contact'
                }
            },
            {
                path: 'privacy-policy', loadChildren: () => import('app/modules/landing/privacy-policy/privacy-policy.routes'),
                data: {
                    title: 'Superformance Privacy Policy',
                    description: 'Read the Superformance privacy policy. Learn how we collect, use, and protect your personal information.',
                    keywords: 'Superformance privacy policy, data protection, personal information, privacy',
                    ogTitle: 'Superformance Privacy Policy',
                    ogDescription: 'Read the Superformance privacy policy. Learn how we collect, use, and protect your personal information.',
                    ogImage: 'https://www.superformance.com/images/logo/logo.svg',
                    ogUrl: 'https://www.superformance.com/privacy-policy',
                    ogType: 'website',
                    twCard: 'summary_large_image',
                    twTitle: 'Superformance Privacy Policy',
                    twDescription: 'Read the Superformance privacy policy. Learn how we collect, use, and protect your personal information.',
                    twImage: 'https://www.superformance.com/images/logo/logo.svg',
                    canonical: 'https://www.superformance.com/privacy-policy'
                }
            },
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            { path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes') },
        ]
    }
];
